/*
 * dailyDrops — curated pool of small once-a-day gifts.
 *
 * Each entry is framed as "an elf left this for you" per LORE.md. None of
 * these drops grant mechanical effects yet — the parent task will wire the
 * `cosmetic-tint` and `mushroom-bonus` kinds into actual game state later.
 * Today they're flavor placeholders that show in the toast and get recorded
 * in `claimedIds` so the no-repeat-within-7-days rule has something to bite on.
 *
 * The named elf caretakers (Aldermere, Tindra, Bramwen, Old Hask, Marrin,
 * the Grove Keeper) come from `docs/LORE.md`. A handful of drops use
 * unnamed-elf attribution ("an elf you don't know") which the lore explicitly
 * allows — Fernhollow has more elves than the doc names.
 */

export type DailyDropKind = 'cosmetic-tint' | 'flavor' | 'mushroom-bonus';

export interface DailyDrop {
  /** Stable id used for the no-repeat-within-7-days check. Never reorder or rename. */
  readonly id: string;
  readonly kind: DailyDropKind;
  /** Short noun phrase, shown bold in the toast. e.g. "A pressed maple leaf". */
  readonly label: string;
  /** The elf this came from. Lowercase / proper-noun as written. */
  readonly elfAttribution: string;
  /** The full one-or-two-sentence flavor line shown under the label. */
  readonly body: string;
}

export const DAILY_DROPS: readonly DailyDrop[] = [
  {
    id: 'pressed-leaf',
    kind: 'flavor',
    label: 'A pressed maple leaf',
    elfAttribution: 'Aldermere',
    body: "Aldermere left this on your pillow. It smells faintly of woodsmoke.",
  },
  {
    id: 'extra-mushroom',
    kind: 'mushroom-bonus',
    label: 'A spotted cap mushroom',
    elfAttribution: 'Old Hask',
    body: "Hask saved one for you. Good for the next forage trip.",
  },
  {
    id: 'lantern-thread',
    kind: 'cosmetic-tint',
    label: 'A length of lantern-yellow thread',
    elfAttribution: 'Tindra',
    body: "Tindra tied this around your wrist while you slept.",
  },
  {
    id: 'acorn-cup',
    kind: 'flavor',
    label: 'A tiny acorn cup',
    elfAttribution: 'Bramwen',
    body: "Bramwen set this by your door. Just the right size for a thimble of dew.",
  },
  {
    id: 'wild-blackberry',
    kind: 'flavor',
    label: 'A single wild blackberry',
    elfAttribution: 'Marrin',
    body: "Marrin left this in a folded paper square. The paper has small writing on it you can't quite read.",
  },
  {
    id: 'moss-tuft',
    kind: 'cosmetic-tint',
    label: 'A tuft of soft moss',
    elfAttribution: 'the Grove Keeper',
    body: "The Grove Keeper tucked this behind your ear before the lanterns were lit.",
  },
  {
    id: 'beeswax-button',
    kind: 'flavor',
    label: 'A small beeswax button',
    elfAttribution: 'Old Hask',
    body: "Hask polished this one until it shone. It still smells of the hive.",
  },
  {
    id: 'pinecone-keepsake',
    kind: 'flavor',
    label: 'A very small pinecone',
    elfAttribution: 'Aldermere',
    body: "Aldermere set this on the windowsill for you. It rolls just slightly.",
  },
  {
    id: 'cricket-feather',
    kind: 'cosmetic-tint',
    label: 'A speckled cricket-feather',
    elfAttribution: 'Tindra',
    body: "Tindra found it on the lantern walk and thought of you.",
  },
  {
    id: 'mushroom-cap-bowl',
    kind: 'mushroom-bonus',
    label: 'A buttoncap, set aside',
    elfAttribution: 'Bramwen',
    body: "Bramwen left this in the dish by your door. One head start for tomorrow's forage.",
  },
  {
    id: 'river-pebble',
    kind: 'flavor',
    label: 'A smooth river pebble',
    elfAttribution: 'the Grove Keeper',
    body: "The Grove Keeper carried this back from the brookside. It's cold, even now.",
  },
  {
    id: 'wishing-thread',
    kind: 'cosmetic-tint',
    label: 'A length of cream-white thread',
    elfAttribution: 'an elf you don\'t know',
    body: "It was looped twice around your paw when you woke. You don't know whose.",
  },
  {
    id: 'lavender-sprig',
    kind: 'flavor',
    label: 'A sprig of dried lavender',
    elfAttribution: 'Marrin',
    body: "Marrin is learning to dry herbs. This one came out very well.",
  },
  {
    id: 'spotted-feather',
    kind: 'cosmetic-tint',
    label: 'A small spotted feather',
    elfAttribution: 'Old Hask',
    body: "Hask says a finch dropped it near the hives. It's a soft brown, dusted with cream.",
  },
  {
    id: 'twilight-ribbon',
    kind: 'cosmetic-tint',
    label: 'A ribbon the color of twilight',
    elfAttribution: 'Tindra',
    body: "Tindra dyed this herself. She wasn't sure if the color would take. It did.",
  },
  {
    id: 'honeycomb-crumb',
    kind: 'flavor',
    label: 'A crumb of honeycomb',
    elfAttribution: 'Old Hask',
    body: "Hask wrapped it in a leaf. It is, as expected, very good.",
  },
  {
    id: 'apothecary-note',
    kind: 'flavor',
    label: 'A small folded note',
    elfAttribution: 'the Grove Keeper',
    body: "The Grove Keeper left this on the apothecary basket. It just says, 'thank you for the mushrooms.'",
  },
  {
    id: 'paper-star',
    kind: 'flavor',
    label: 'A folded paper star',
    elfAttribution: 'an elf you don\'t know',
    body: "Someone left this on the well's lip. It's the size of a thumbnail. It's clearly for you.",
  },
];

if (DAILY_DROPS.length < 15) {
  throw new Error(
    `DAILY_DROPS pool is too small (${DAILY_DROPS.length}); spec requires 15+.`
  );
}
